import React from "react";
import * as finService from "../../services/finService";
import useModuleAccess from "../../hooks/useModuleAccess";
import { isModuleAdmin } from "../../constants/platformModules";
import MasterSection from "../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "./useFinOptions";
import { fmtMoney } from "./gl/glContext";
import { PageHeader, formatDate, pageStyle } from "../../components/ui";

// Projects (internal orders / WBS): a launch event, a showroom refit, a marketing campaign.
// Each project is also a value of the chart of accounts' Project segment, so costs and
// revenue can be reported by project.
const TYPES = [
  { label: "Internal order", value: "INTERNAL_ORDER" },
  { label: "Work breakdown (WBS)", value: "WBS" },
  { label: "Campaign", value: "CAMPAIGN" },
  { label: "Launch", value: "LAUNCH" },
];
const typeLabel = (v) => (TYPES.find((t) => t.value === v) || {}).label || v;

export default function FinProjects() {
  const { access } = useModuleAccess();
  const opts = useFinOptions(["projects", "currencies"]);
  return (
    <div style={pageStyle}>
      <PageHeader title="Projects" subtitle="Internal orders and work breakdown, reported through the Project segment" />
      <MasterSection
        load={() => finService.listMaster("projects")}
        save={(id, data) => finService.saveMaster("projects", id, data)}
        noun="project"
        canEdit={isModuleAdmin(access, "FIN")}
        columns={[
          ["Code", (r) => r.code],
          ["Name", (r) => r.name],
          ["Type", (r) => typeLabel(r.project_type)],
          ["Owner", (r) => r.owner_employee_name],
          ["Budget", (r) => (r.budget_amount ? `${fmtMoney(r.budget_amount)} ${r.budget_currency || ""}` : "—")],
          ["Dates", (r) => (r.start_date ? `${formatDate(r.start_date)} – ${r.end_date ? formatDate(r.end_date) : "…"}` : "—")],
        ]}
        fieldKeys={["name", "project_type", "parent_id", "owner_employee_id", "budget_amount", "budget_currency", "start_date", "end_date"]}
        lockedOnEdit={["code"]}
        defaults={{ project_type: "INTERNAL_ORDER" }}
        toForm={(r) => ({ ...r, parent_id: optionByCode(opts.projects, r.parent_code) })}
        fields={() => [
          { key: "code", label: "Code (the Project segment value)", required: true, max: 25, hint: "Letters and digits. Cannot be changed later." },
          { key: "name", label: "Name", required: true, max: 150 },
          { key: "project_type", label: "Type", type: "select", required: true, options: TYPES },
          { key: "parent_id", label: "Part of project", type: "select", options: opts.projects || [] },
          { key: "owner_employee_id", label: "Owner", type: "employee" },
          { key: "budget_amount", label: "Budget", type: "number", step: "any", min: 0 },
          { key: "budget_currency", label: "Budget currency", type: "select", options: (opts.currencies || []).map((o) => ({ label: o.label, value: o.code })) },
          { key: "start_date", label: "Start", type: "date" },
          { key: "end_date", label: "End", type: "date" },
        ]}
      />
    </div>
  );
}
